// src/components/PoolCreationForm.jsx
import React, { useState, useContext } from 'react';
import { ContractContext } from '../contexts/ContractContext';

const PoolCreationForm = ({ onSuccess }) => {
  const { createPool } = useContext(ContractContext);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // Pool configuration options
  const poolSizes = [
    { id: 'tiny', name: 'Tiny Triumph', value: 1, fee: 0.1, limit: 1 },
    { id: 'medium', name: 'Halfway Heaven', value: 5, fee: 0.5, limit: 1 },
    { id: 'large', name: 'Big Bucks Bonanza', value: 10, fee: 1, limit: 2 }
  ];
  
  const durationOptions = [
    { value: 1, label: '1 hour' },
    { value: 3, label: '3 hours' },
    { value: 12, label: '12 hours' },
    { value: 24, label: '24 hours' },
    { value: 0, label: 'Until filled' }
  ];
  
  // Form state
  const [selectedSize, setSelectedSize] = useState(poolSizes[0]);
  const [duration, setDuration] = useState(durationOptions[0].value);
  
  // Fee percentages (fixed values)
  const creatorFee = 50;  // 5%
  const platformFee = 30; // 3%
  const gasFee = 20;      // 2%
  const prizePercent = 90; // 90%
  
  // Calculate pool summary
  const calculateSummary = () => {
    const totalEntries = Math.round(selectedSize.value / selectedSize.fee);
    const totalPool = selectedSize.value;
    const maxPrize = totalPool * (prizePercent / 100);
    const creatorFeeAmount = totalPool * (5 / 100);
    
    return {
      totalPool,
      totalEntries,
      maxPrize: maxPrize.toFixed(1),
      creatorFeeAmount: creatorFeeAmount.toFixed(1)
    };
  };
  
  const summary = calculateSummary();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);
    
    try {
      await createPool({
        entryFee: selectedSize.fee,
        maxEntriesPerUser: selectedSize.limit,
        maxEntries: Math.round(selectedSize.value / selectedSize.fee),
        duration,
        creatorFee,
        platformFee,
        gasFee
      });
      
      alert('Pool created successfully! As the creator, you will receive 5% of the total pool, but you cannot participate in it.');
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error creating pool:', err);
      setError('Failed to create pool: ' + err.message);
    } finally {
      setIsProcessing(false);
    }
  };
  
  return (
    <div>
      <h2>Create New Pool</h2>
      <p className="subtitle">Configure your lottery pool with the options below</p>
      
      {error && <div className="alert alert-danger">{error}</div>}
      
      <form id="create-pool-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>Pool Size</h3>
          <div className="option-group">
            {poolSizes.map(size => (
              <div 
                key={size.id}
                className={`option-card ${selectedSize.id === size.id ? 'selected' : ''}`}
                onClick={() => setSelectedSize(size)}
              >
                <h4>{size.name}</h4>
                <p>{size.value} MONAD total</p>
                <p className="detail">Entry fee: {size.fee} MONAD</p>
              </div>
            ))}
          </div>
        </div>
        
        <div className="form-section">
          <h3>Duration</h3>
          <div className="radio-group">
            {durationOptions.map(option => (
              <label 
                key={option.value}
                className={`radio-option ${duration === option.value ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="duration"
                  value={option.value}
                  checked={duration === option.value}
                  onChange={() => setDuration(option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </div>
        
        <div className="form-section">
          <h3>Entry Limit per User</h3>
          <p className="info-text">
            Each user can purchase up to <span id="max-entries-display">{selectedSize.limit}</span> entries
          </p>
          <p className="detail-text">This is automatically calculated based on pool size</p>
        </div>
        
        <div className="form-section">
          <h3>Fee Distribution</h3>
          <p className="info-text">Fixed percentages ensure fair distribution</p>
          <div className="fee-distribution">
            <div className="fee-item">
              <div className="fee-label">Platform Fee</div>
              <div className="fee-value">3%</div>
            </div>
            <div className="fee-item">
              <div className="fee-label">Creator Fee</div>
              <div className="fee-value">5%</div>
            </div>
            <div className="fee-item">
              <div className="fee-label">Gas Fee</div>
              <div className="fee-value">2%</div>
            </div>
            <div className="fee-item highlight">
              <div className="fee-label">Prize Pool</div>
              <div className="fee-value">90%</div>
            </div>
          </div>
        </div>
        
        <div className="form-section summary">
          <h3>Pool Summary</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <div className="summary-label">Total Pool Size</div>
              <div className="summary-value">{summary.totalPool} MONAD</div>
            </div>
            <div className="summary-item">
              <div className="summary-label">Number of Entries</div>
              <div className="summary-value">{summary.totalEntries}</div>
            </div>
            <div className="summary-item">
              <div className="summary-label">Max Prize</div>
              <div className="summary-value">{summary.maxPrize} MONAD</div>
            </div>
            <div className="summary-item">
              <div className="summary-label">Your Creator Fee</div>
              <div className="summary-value">{summary.creatorFeeAmount} MONAD</div>
            </div>
          </div>
        </div>
        
        <button 
          type="submit" 
          className="create-button"
          disabled={isProcessing}
        >
          {isProcessing ? 'Creating Pool...' : 'Create Pool'}
        </button>
      </form>
    </div>
  );
};

export default PoolCreationForm;